import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Select, MenuItem, FormControl, InputLabel,
  IconButton, Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import api from '../services/api';
import { fmtDateTimeSeconds } from '../utils/formatDate';

const ACTION_COLORS = {
  create: 'success', delete: 'error', update: 'info',
  generate_bulk: 'warning', email: 'info', cancel: 'default',
  restart: 'warning', revoke: 'error', login: 'default',
};

const RESOURCE_LABELS = {
  dcp_job: 'DCP Job', kdm: 'KDM', venue: 'Venue', screener: 'Watermark Encode',
  delivery: 'Delivery', user: 'User', share: 'Share',
};

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resourceFilter, setResourceFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (resourceFilter) params.resource_type = resourceFilter;
      if (actionFilter) params.action = actionFilter;
      params.limit = 200;
      const { data } = await api.get('/api/audit/', { params });
      setLogs(data);
    } catch (_err) {
      // fail silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [resourceFilter, actionFilter]);

  return (
    <Box>
      <PageHeader
        title="Audit Log"
        subtitle="System activity and user actions"
        actions={
          <Tooltip title="Refresh"><IconButton onClick={fetchLogs}><RefreshIcon /></IconButton></Tooltip>
        }
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Resource</InputLabel>
          <Select value={resourceFilter} label="Resource" onChange={(e) => setResourceFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="dcp_job">DCP Jobs</MenuItem>
            <MenuItem value="kdm">KDMs</MenuItem>
            <MenuItem value="venue">Venues</MenuItem>
            <MenuItem value="screener">Watermark Encodes</MenuItem>
            <MenuItem value="delivery">Delivery</MenuItem>
            <MenuItem value="user">Users</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Action</InputLabel>
          <Select value={actionFilter} label="Action" onChange={(e) => setActionFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="create">Create</MenuItem>
            <MenuItem value="delete">Delete</MenuItem>
            <MenuItem value="update">Update</MenuItem>
            <MenuItem value="generate_bulk">Generate (Bulk)</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="cancel">Cancel</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Resource</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell>
                  <Typography variant="caption">
                    {fmtDateTimeSeconds(log.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{log.username || `User #${log.user_id}`}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={log.action} color={ACTION_COLORS[log.action] || 'default'} size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {RESOURCE_LABELS[log.resource_type] || log.resource_type}
                    {log.resource_id ? ` #${log.resource_id}` : ''}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 400 }}>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <Typography variant="caption" sx={{ wordBreak: 'break-word', color: 'text.secondary' }}>
                      {Object.entries(log.details).map(([k, v]) => (
                        <span key={k}><strong>{k}</strong>: {typeof v === 'object' ? JSON.stringify(v) : String(v)} &nbsp;</span>
                      ))}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    {log.ip_address || '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{loading ? 'Loading...' : 'No audit entries found.'}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default AuditLog;
